import SideMenuButton from "@navigation/SideMenuButton";
import MenuButton from "./MenuButton";
import MenuInception from "./MenuInception";
import { Project, ProjectHistoryEvent } from "types";
import { Timestamp } from "firebase/firestore";
import { showNotification } from "context/notification.service";
import { User } from "types/user";
import { useProjectStore } from "@store/projectStore";
import { canDeleteProject } from "@utils/permissions";

const projectStatuses = [
  { value: "in_progress", label: "En cours" },
  { value: "delivered", label: "Livré" },
  { value: "sav", label: "SAV" },
] as const;

const statusLabels: Record<string, string> = {
  not_started: "En attente",
  in_progress: "En cours",
  delivered: "Livré",
  sav: "SAV",
};

export const ActionsMenuButton = ({
  project,
  setProject,
  modalsOpen,
  setModalsOpen,
  userData,
  isGuest = false,
  contactsMetadata,
  onResendAccess,
  onResetPassword,
  onEditClientInfos,
}: {
  project: Project;
  setProject: (project: Project) => void;
  modalsOpen: {
    modify: boolean;
    delete: boolean;
    addDocument: boolean;
    addPhoto: boolean;
    compteRendu: boolean;
    changeStatus: boolean;
    sendEmail: boolean;
  };
  setModalsOpen: React.Dispatch<
    React.SetStateAction<{
      modify: boolean;
      delete: boolean;
      addDocument: boolean;
      addPhoto: boolean;
      compteRendu: boolean;
      changeStatus: boolean;
      sendEmail: boolean;
    }>
  >;
  userData?: User | null;
  isGuest?: boolean;
  contactsMetadata?: Map<string, { status: string; has_third_party: boolean }>;
  onResendAccess?: (contact: { user_id: string; first_name: string; last_name: string; email?: string }) => void;
  onResetPassword?: (contact: { user_id: string; first_name: string; last_name: string }) => void;
  onEditClientInfos?: () => void;
}) => {
  const { updateProject } = useProjectStore();

  const handleChangeStatus = (newStatus: string) => {
    if (project.status === newStatus) return;

    const historyEvent: ProjectHistoryEvent = {
      id: `event_${Date.now()}`,
      action: `a changé le statut en "${statusLabels[newStatus] || newStatus}"`,
      user: {
        id: userData?.id || "unknown",
        display_name: userData?.infos.display_name || "Utilisateur",
        photo_url: userData?.customization.photo_url,
      },
      date: Timestamp.now(),
    };

    const updated = {
      ...project,
      status: newStatus,
      history_events: [historyEvent, ...project.history_events],
    };

    setProject(updated);
    updateProject(updated);

    showNotification({
      type: "success",
      message: `Statut changé en "${statusLabels[newStatus] || newStatus}"`,
    });
  };

  const contacts = project.infos.client.contacts;
  const contactsWithUserId = contacts.filter((c) => c.user_id);
  const contactsWithEmail = contacts.filter((c) => c.email?.trim());

  return (
    <>
      <MenuButton label="Actions">
        <div className="flex flex-col ">
          {!isGuest && (
            <div className="flex flex-col p-1">
              <div className="text-h2 text-neutral-400 p-2">ACTIONS RAPIDES</div>
              <SideMenuButton
                size="large"
                label="Modifier"
                name="modify"
                onClick={() => setModalsOpen({ ...modalsOpen, modify: true })}
              ></SideMenuButton>
              {canDeleteProject(userData) && (
                <SideMenuButton
                  size="large"
                  label="Supprimer"
                  name="delete"
                  onClick={() => setModalsOpen({ ...modalsOpen, delete: true })}
                ></SideMenuButton>
              )}
            </div>
          )}
          {!isGuest && (
            <div className="flex flex-col border-t-1 p-1 ">
              <div className="text-h2 text-neutral-400 p-2">COMMUNICATION</div>
              <SideMenuButton
                size="large"
                label="Envoyer un email"
                name="email"
                onClick={() =>
                  setModalsOpen({ ...modalsOpen, sendEmail: true })
                }
              ></SideMenuButton>
            </div>
          )}
          {isGuest && onEditClientInfos && (
            <div className="flex flex-col border-t-1 p-1">
              <div className="text-h2 text-neutral-400 p-2">MON COMPTE</div>
              <SideMenuButton
                size="large"
                label="Modifier mes informations"
                name="modify"
                onClick={onEditClientInfos}
              />
            </div>
          )}
          <div className="flex flex-col border-t-1 p-1 ">
            <div className="text-h2 text-neutral-400 p-2">DOSSIER</div>
            <SideMenuButton
              size="large"
              label="Ajouter document"
              name="plus"
              onClick={() =>
                setModalsOpen({ ...modalsOpen, addDocument: true })
              }
            ></SideMenuButton>
            <SideMenuButton
              size="large"
              label="Ajouter photo"
              name="plus"
              onClick={() => setModalsOpen({ ...modalsOpen, addPhoto: true })}
            ></SideMenuButton>
            {!isGuest && (
              <>
                <SideMenuButton
                  size="large"
                  label="Compte rendu"
                  name="file"
                  onClick={() =>
                    setModalsOpen({ ...modalsOpen, compteRendu: true })
                  }
                ></SideMenuButton>
                <MenuInception
                  size="large"
                  label="Changer le statut"
                  name="restart"
                >
                  <div className="flex flex-col">
                    {projectStatuses.map((s) => (
                      <SideMenuButton
                        key={s.value}
                        size="large"
                        label={s.label}
                        name={project.status === s.value ? "done2" : undefined}
                        onClick={() => handleChangeStatus(s.value)}
                      />
                    ))}
                  </div>
                </MenuInception>
              </>
            )}
          </div>
          {!isGuest && contactsWithEmail.length > 0 && (
            <div className="flex flex-col border-t-1 p-1 ">
              <div className="text-h2 text-neutral-400 p-2">CLIENT</div>
              <MenuInception
                size="large"
                label="Renvoyer l'invitation / accès"
                name="email"
              >
                <div className="flex flex-col">
                  {contactsWithEmail.map((contact, idx) => (
                    <SideMenuButton
                      key={contact.user_id || contact.email || idx}
                      size="large"
                      label={`${contact.first_name} ${contact.last_name}`}
                      name="user"
                      onClick={() =>
                        onResendAccess?.({
                          user_id: contact.user_id || "",
                          first_name: contact.first_name,
                          last_name: contact.last_name,
                          email: contact.email,
                        })
                      }
                    />
                  ))}
                </div>
              </MenuInception>
              {contactsWithUserId.some(
                (c) => c.user_id && contactsMetadata?.get(c.user_id) && !contactsMetadata.get(c.user_id)!.has_third_party
              ) && (
                <MenuInception
                  size="large"
                  label="Réinitialiser le mot de passe"
                  name="lock"
                >
                  <div className="flex flex-col">
                    {contactsWithUserId
                      .filter(
                        (c) =>
                          c.user_id &&
                          contactsMetadata?.get(c.user_id) &&
                          !contactsMetadata.get(c.user_id)!.has_third_party
                      )
                      .map((contact) => (
                        <SideMenuButton
                          key={contact.user_id}
                          size="large"
                          label={`${contact.first_name} ${contact.last_name}`}
                          name="user"
                          onClick={() =>
                            onResetPassword?.({
                              user_id: contact.user_id!,
                              first_name: contact.first_name,
                              last_name: contact.last_name,
                            })
                          }
                        />
                      ))}
                  </div>
                </MenuInception>
              )}
            </div>
          )}
        </div>
      </MenuButton>
    </>
  );
};
